import { Box, Typography, ButtonGroup, Button } from "@mui/material";
import MoedaText from "../ui/inputs/moedaText";
import { filter, map } from "lodash";
import InteiroText from "../ui/inputs/inteiroText";
import TabelaBase from "./tabelaBase";
import Ticker from "../ui/ticker";
import {
  OperacaoLeitorBMF,
  OperacaoLeitorBovespa,
} from "../../../models/operacao";
import { IconFileSearch } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

type Props = {
  operacoes: (OperacaoLeitorBovespa | OperacaoLeitorBMF)[];
  showDate?: boolean;
};

export default function TabelaOperacoesExtendida({
  operacoes,
  showDate = false,
}: Props) {
  const navigate = useNavigate();
  const header: {
    titulo: string;
    key: string;
    ordem: number;
    align: "left" | "center" | "right";
  }[] = filter(
    [
      { titulo: "Data", key: "data", ordem: 1, align: "center" },
      { titulo: "Mercado", key: "mercado", ordem: 2, align: "center" },
      { titulo: "C/V", key: "cv", ordem: 3, align: "center" },
      { titulo: "Ativo", key: "ativo", ordem: 4, align: "center" },
      { titulo: "Qtd.", key: "quantidade", ordem: 5, align: "right" },
      { titulo: "Preço", key: "precoUnitario", ordem: 6, align: "right" },
      { titulo: "Total", key: "valorTotal", ordem: 7, align: "right" },
      { titulo: "", key: "acoes", ordem: 8, align: "right" },
    ],
    (column) => {
      if (column.key === "data" && !showDate) return false;
      return true;
    }
  );

  const rows = map(operacoes, (operacao) => ({
    data: celulaData(operacao),
    ativo: celulaAtivo(operacao),
    cv: celulaCV(operacao),
    mercado: celulaMercado(operacao),
    quantidade: celulaQuantidade(operacao),
    precoUnitario: celulaPrecoUnitario(operacao),
    valorTotal: celulaValorTotal(operacao),
    acoes: celulaAcoes(operacao),
  }));

  return <TabelaBase header={header} rows={rows} />;

  function celulaData(
    operacao: OperacaoLeitorBovespa | OperacaoLeitorBMF
  ): JSX.Element {
    return celulaMono(operacao.dataPregao.format("DD/MM/YYYY"));
  }

  function celulaMercado(
    operacao: OperacaoLeitorBovespa | OperacaoLeitorBMF
  ): JSX.Element {
    return celulaTexto(operacao.mercado);
  }

  function celulaCV(
    operacao: OperacaoLeitorBovespa | OperacaoLeitorBMF
  ): JSX.Element {
    return celulaTexto(operacao.tipoOperacao);
  }

  function celulaAtivo(
    operacao: OperacaoLeitorBovespa | OperacaoLeitorBMF
  ): JSX.Element {
    return (
      <Box
        display="flex"
        flexDirection={"row"}
        justifyContent="center"
        alignItems={"baseline"}
      >
        <Ticker codigoAtivo={operacao.codigoAtivo} color="primary" />
        {"codigoObjeto" in operacao && operacao.codigoObjeto && (
          <>
            <Box paddingLeft={1} />
            <Ticker codigoAtivo={`➡️ ${operacao.codigoObjeto}`} />
          </>
        )}
        {operacao.prazo && (
          <>
            <Box paddingLeft={1} />
            <Ticker
              codigoAtivo={`📆 ${operacao.prazo?.format("DD/MM/YYYY")}`}
            />
          </>
        )}
      </Box>
    );
  }

  function celulaQuantidade(
    operacao: OperacaoLeitorBovespa | OperacaoLeitorBMF
  ): JSX.Element {
    return (
      <Typography variant="dataMono">
        <InteiroText
          value={operacao.quantidade}
          displayType={"text"}
        ></InteiroText>
      </Typography>
    );
  }

  function celulaPrecoUnitario(
    operacao: OperacaoLeitorBovespa | OperacaoLeitorBMF
  ): JSX.Element {
    return (
      <Typography variant="dataMono">
        <MoedaText
          displayType={"text"}
          value={operacao.precoUnitario}
        ></MoedaText>
      </Typography>
    );
  }

  function celulaValorTotal(
    operacao: OperacaoLeitorBovespa | OperacaoLeitorBMF
  ): JSX.Element {
    return (
      <Typography variant="dataMono">
        <MoedaText
          displayType={"text"}
          value={
            "valorTotal" in operacao
              ? operacao.valorTotal
              : operacao.valorOperacao
          }
        ></MoedaText>
      </Typography>
    );
  }

  function celulaAcoes(
    operacao: OperacaoLeitorBovespa | OperacaoLeitorBMF
  ): JSX.Element {
    return (
      <ButtonGroup size="small" variant="outlined">
        <Button
          size="small"
          color="info"
          onClick={() => navigate(`/arquivo/nota/${operacao.nota}`)}
        >
          <IconFileSearch size={16} />
        </Button>
      </ButtonGroup>
    );
  }

  function celulaTexto(texto?: string): JSX.Element {
    return <Typography>{texto}</Typography>;
  }

  function celulaMono(texto?: string): JSX.Element {
    return <Typography variant="dataMono">{texto}</Typography>;
  }
}
